import type { DocumentReference } from "fhir/r5";
import {
  extractDocumentTransactionId,
  extractDocumentVerification,
  extractRejectionReason,
  type DocumentVerificationInfo,
} from "src-next/modules/patient-supporting-documents";
import { formatDateLabel } from "./documentDisplay";
import type { DocumentCategory } from "../types";
import { pickLatestDocument } from "./documentTransactions";

export function collectApprovedDocIds(
  categories: DocumentCategory[],
  organizationId?: string,
): string[] {
  return categories.flatMap((category) =>
    category.docs
      .filter(
        (document) =>
          extractDocumentVerification(document, organizationId).status ===
          "approved",
      )
      .map((document) => document.id)
      .filter((id): id is string => Boolean(id)),
  );
}

export function resolveDocsReleaseState(
  categories: DocumentCategory[],
  organizationId?: string,
): { canRelease: boolean; allApproved: boolean; hasRejected: boolean } {
  const withDocs = categories.filter((category) => category.docs.length > 0);
  const allDocs = withDocs.flatMap((category) => category.docs);
  if (!allDocs.length) {
    return { canRelease: false, allApproved: false, hasRejected: false };
  }

  let hasPending = false;
  let hasRejected = false;
  let allApproved = true;

  for (const document of allDocs) {
    const status = extractDocumentVerification(document, organizationId).status;
    if (status === "pending" || status === "none") hasPending = true;
    if (status === "rejected") hasRejected = true;
    if (status !== "approved") allApproved = false;
  }

  return {
    canRelease: !hasPending,
    allApproved,
    hasRejected,
  };
}

export type CategoryStatus = "empty" | "pending" | "approved" | "rejected" | "mixed";
export type DocStatus = "pending" | "approved" | "rejected";

export function resolveCategoryStatus(
  documents: DocumentReference[],
  organizationId?: string,
): CategoryStatus {
  if (!documents.length) return "empty";
  let approved = 0;
  let rejected = 0;
  for (const document of documents) {
    const status = extractDocumentVerification(document, organizationId).status;
    if (status === "approved") approved += 1;
    else if (status === "rejected") rejected += 1;
  }
  if (approved === documents.length) return "approved";
  if (rejected === documents.length) return "rejected";
  if (approved === 0 && rejected === 0) return "pending";
  return "mixed";
}

export function resolveDocStatus(
  document: DocumentReference,
  organizationId?: string,
): DocStatus {
  const status = extractDocumentVerification(document, organizationId).status;
  if (status === "approved") return "approved";
  if (status === "rejected") return "rejected";
  return "pending";
}

export function docDisplayTitle(
  document: DocumentReference,
  index: number,
): string {
  return document.description?.trim() || `Arquivo ${index + 1}`;
}

export function docMetaLine(
  document: DocumentReference,
  verification: DocumentVerificationInfo,
  statusKey: DocStatus,
): string | null {
  if (statusKey === "approved" && verification.attestedAt) {
    const validity = verification.validUntil
      ? ` · válido até ${formatDateLabel(verification.validUntil)}`
      : "";
    return `Verificado em ${formatDateLabel(verification.attestedAt)}${validity}`;
  }
  if (statusKey === "rejected") {
    return extractRejectionReason(document) || "Recusado pela loja";
  }
  const sentAt = formatDateLabel(document.date);
  return sentAt ? `Enviado em ${sentAt}` : null;
}

/**
 * Recorta categorias para o contexto da transação.
 * Pessoal/residência: latest (herdado se aprovado).
 * Receita/ANVISA: só docs com o mesmo transaction-id.
 */
export function filterCategoriesForTransaction(
  categories: DocumentCategory[],
  transactionId?: string,
): { categories: DocumentCategory[]; inheritedDocIds: string[] } {
  const inheritedDocIds: string[] = [];
  if (!transactionId || transactionId.startsWith("pending:")) {
    return {
      categories: categories.map((category) => ({ ...category, docs: [] })),
      inheritedDocIds,
    };
  }

  const next = categories.map((category) => {
    const isShared =
      category.code === "personal_document" ||
      category.code === "proof_of_residence";

    if (isShared) {
      const latest = pickLatestDocument(category.docs);
      if (latest?.id) inheritedDocIds.push(latest.id);
      return {
        ...category,
        docs: latest ? [latest] : [],
      };
    }

    const matched = category.docs.filter(
      (document) => extractDocumentTransactionId(document) === transactionId,
    );

    return { ...category, docs: matched };
  });

  return { categories: next, inheritedDocIds };
}

export function docFileLabel(document: DocumentReference): string {
  const attachment = document.content?.[0]?.attachment;
  const contentType = attachment?.contentType?.toLowerCase() ?? "";

  if (contentType.includes("pdf")) return "PDF";
  if (contentType.includes("png")) return "PNG";
  if (contentType.includes("jpeg") || contentType.includes("jpg")) return "JPG";
  if (contentType.includes("webp")) return "WEBP";

  const fileName =
    attachment?.title?.trim() ||
    attachment?.url?.split("/").pop()?.split("?")[0];

  if (!fileName) return "Documento";
  if (fileName.length <= 22) return fileName;
  return `${fileName.slice(0, 19)}…`;
}
